import { motion, AnimatePresence } from "framer-motion";
import { formatUSD, formatTime } from "@/lib/format";

const ACTION_META = {
  buy:         { label: "BUY",        cls: "bg-profit text-white" },
  sell:        { label: "SELL",       cls: "bg-loss text-white" },
  hold:        { label: "HOLD",       cls: "bg-arena-alt text-arena-soft" },
  buy_call:    { label: "BUY CALL",   cls: "bg-profit text-white" },
  buy_put:     { label: "BUY PUT",    cls: "bg-loss text-white" },
  close_call:  { label: "CLOSE CALL", cls: "bg-arena-ink text-white" },
  close_put:   { label: "CLOSE PUT",  cls: "bg-arena-ink text-white" },
  expire_call: { label: "CALL EXPIRY",cls: "border border-arena-line text-arena-soft" },
  expire_put:  { label: "PUT EXPIRY", cls: "border border-arena-line text-arena-soft" },
};

const formatExpiry = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${m}/${d}`;
};

export default function TradeFeed({ trades }) {
  return (
    <section data-testid="trade-feed" className="border border-arena-line bg-white flex flex-col h-full">
      <div className="px-6 lg:px-8 py-4 border-b border-arena-line flex items-center justify-between">
        <h2 className="font-display font-black text-2xl tracking-tighter">LIVE FEED</h2>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 bg-profit live-dot" />
          <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-arena-soft">Streaming</span>
        </div>
      </div>

      <div className="overflow-y-auto feed-scroll" style={{ maxHeight: 560 }}>
        {trades.length === 0 ? (
          <div className="p-8 text-center text-arena-soft text-sm font-mono">
            No trades yet — click RUN ROUND to begin.
          </div>
        ) : (
          <AnimatePresence initial={false}>
            {trades.map((t) => {
              const meta = ACTION_META[t.action] || ACTION_META.hold;
              const isOption = (t.option_meta && t.option_meta.type) ||
                t.action.startsWith("buy_") || t.action.startsWith("close_") ||
                t.action.startsWith("expire_");
              return (
                <motion.div
                  key={t.id}
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  data-testid={`trade-${t.id}`}
                  className="border-b border-arena-line/60 px-6 lg:px-8 py-4 hover:bg-arena-alt/50 transition-colors"
                >
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className={`px-2 py-[3px] text-[10px] font-mono font-bold tracking-[0.2em] ${meta.cls}`}>
                      {meta.label}
                    </span>
                    <span
                      className="font-display font-black text-sm tracking-tight"
                      style={{ color: t.trader_color }}
                    >
                      {t.trader_name}
                    </span>
                    {t.action !== "hold" && t.symbol && (
                      <span className="font-mono font-bold text-sm tabular-nums">
                        {t.qty}{isOption ? "c" : ""} × {t.symbol}
                        {isOption && t.option_meta && (
                          <span className="text-arena-soft ml-1">
                            ${t.option_meta.strike?.toFixed?.(0) || t.option_meta.strike} {formatExpiry(t.option_meta.expiry)}
                          </span>
                        )}
                      </span>
                    )}
                    {t.executed && t.action !== "hold" && (
                      <span className="font-mono text-xs tabular-nums text-arena-soft">
                        @ {formatUSD(t.price)} = {formatUSD(t.total)}
                      </span>
                    )}
                    <span className="ml-auto text-[10px] font-mono uppercase tracking-[0.2em] text-arena-soft">
                      {formatTime(t.timestamp)}
                    </span>
                  </div>
                  <blockquote
                    className="mt-3 pl-3 border-l-2 text-xs text-arena-ink leading-relaxed font-ui italic"
                    style={{ borderColor: t.trader_color }}
                  >
                    "{t.reasoning}"
                  </blockquote>
                </motion.div>
              );
            })}
          </AnimatePresence>
        )}
      </div>
    </section>
  );
}
